import { useEffect, useRef, useState } from 'react';
import { avisarFalloDeCarga } from '../lib/fallos';
import { localeUI } from '../lib/format';
import { useSearchParams } from 'react-router-dom';
import { Ban, Camera, Check, ChevronDown, ChevronRight, ClipboardList, FileText, Pencil, Trash2, Video, Wrench } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ScheduleModal } from './ScheduleModal';
import { RequestVisitButtons } from './RequestVisitButtons';
import { LorConfirmButton } from './LorConfirmButton';
import { REQUEST_VISIT_TYPES, REQUEST_TYPE_TO_APPT_TYPE } from '../lib/visitType';

type VisitReq = { id: string; type: string | null; label: string | null; requested_by: string | null; at: string; canceled_at?: string | null; canceled_by?: string | null; cancel_reason?: string | null; fulfilled?: boolean };

/**
 * Fase E (Scheduling): citas del claim. Listar + crear + cambiar estatus +
 * pedir agendamiento (cola hacia ClaimRoute). Aditivo.
 */
type Appt = {
  id: string;
  type: string;
  status: string;
  progress: string;
  scheduled_start: string | null;
  inspector_name: string | null;
  notes: string | null;
  // Reporte del inspector (importado de ClaimRoute).
  result_notes: string | null;
  on_my_way_at: string | null;
  checked_in_at: string | null;
  started_at: string | null;
  checked_out_at: string | null;
  video_url: string | null;
  /** Los videos que el inspector subió desde el app de campo (ATR e informativos), pegados a esta visita. */
  videos?: Array<{ id: string; kind: 'atr' | 'info'; title: string | null; fileName: string; sizeBytes: number | null; mediaUrl: string; readyAt: string | null }>;
  check_photo_url: string | null;
  no_show_photo_url: string | null;
  no_show_reported_at: string | null;
  reschedule_cause: string | null;
  reschedule_urgent?: boolean;
  contractor_name: string | null;
  leg_distance_miles: number | null;
  // Feedback del inspector al checkout de un ATR (condición del techo + su opinión).
  atr_roof_condition: string | null;
  atr_opinion: string | null;
  // Auditoría de cancelación: quién la canceló y cuándo.
  canceled_by_name: string | null;
  canceled_at: string | null;
};
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-US') : '—');

export function ClaimAppointmentsPanel(
  { claimId, clientName, atrSince, inspectorNotes }: { claimId: string; clientName?: string | null; atrSince?: string | null; inspectorNotes?: string | null },
) {
  const { t } = useI18n();
  const [appts, setAppts] = useState<Appt[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [reschedAppt, setReschedAppt] = useState<string | null>(null);
  const [reschedUrgent, setReschedUrgent] = useState(false);

  const [searchParams] = useSearchParams();
  const reportRef = useRef<HTMLDivElement>(null);

  const [visitReqs, setVisitReqs] = useState<VisitReq[]>([]);

  async function load() {
    setLoading(true);
    try { setAppts((await api.get<{ appointments: Appt[] }>(`/api/claims/${claimId}/appointments`)).appointments); }
    catch { setAppts([]); avisarFalloDeCarga('appointments'); } finally { setLoading(false); }
    api.get<{ requests: VisitReq[] }>(`/api/claims/${claimId}/visit-requests`).then((r) => setVisitReqs(r.requests)).catch(() => avisarFalloDeCarga('appointments'));
  }
  useEffect(() => { load(); }, [claimId]);

  // Deep-link desde el calendario (?report=<apptId>): auto-expande el reporte de esa inspección
  // y hace scroll hacia él en cuanto cargan las citas.
  useEffect(() => {
    const rid = searchParams.get('report');
    if (!rid || loading) return;
    if (!appts.some((a) => a.id === rid)) return;
    setExpanded((prev) => (prev.has(rid) ? prev : new Set(prev).add(rid)));
    const el = reportRef.current;
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, appts, searchParams]);

  async function setStatus(id: string, status: string) {
    await api.patch(`/api/appointments/${id}`, { status });
    await load();
  }
  // Nota POR ESTA inspección (appt.notes): el inspector ve esta, no la del claim → un reagendado
  // no arrastra la nota de la inspección pasada.
  const [noteEdits, setNoteEdits] = useState<Record<string, string>>({});
  const [savedNote, setSavedNote] = useState<string | null>(null);
  async function saveNote(id: string, notes: string) {
    try { await api.patch(`/api/appointments/${id}`, { notes }); setSavedNote(id); setTimeout(() => setSavedNote(null), 2000); await load(); } catch { /* */ }
  }
  // "Request scheduling" ya NO manda el request al instante (creaba solicitudes sin instrucciones
  // y duplicadas): elegir un tipo ABRE el formulario de notas de abajo (mismo flujo que los botones).
  const [extOpen, setExtOpen] = useState<{ type: string; n: number } | null>(null);
  function toggleExpand(id: string) {
    setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  /** Elimina (soft) una solicitud pedida por error: pide el MOTIVO y el record queda visible. */
  async function cancelVisitReq(id: string) {
    const reason = window.prompt(t('cd.reqDeleteReason'));
    if (reason === null) return; // canceló el prompt
    if (!reason.trim()) { alert(t('cd.reqDeleteReasonRequired')); return; }
    try { await api.post(`/api/claims/${claimId}/visit-requests/${id}/cancel`, { reason: reason.trim() }); await load(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
  }

  if (loading) return null;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2 className="card-title" style={{ margin: 0 }}>{t('cd.appointments')}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Picker: pregunta cuál visita se necesita antes de mandarla a la cola. */}
          <select className="input sm" value="" onChange={(e) => { if (e.target.value) setExtOpen({ type: e.target.value, n: Date.now() }); e.target.value = ''; }} title={t('cd.reqSched')}>
            <option value="">{t('cd.reqSched')}…</option>
            {REQUEST_VISIT_TYPES.map((v) => <option key={v.type} value={v.type}>{t(v.labelKey)}</option>)}
          </select>
          <button className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', fontWeight: 700 }} onClick={() => setScheduleOpen(true)}>{t('cr.schedule')}</button>
        </div>
      </div>

      {/* Request a visit (ATR / Reinspection / Check pickup / Material sample) ARRIBA de las citas.
          Cada solicitud deja rastro "requested by X" y va armando el historial debajo. */}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
        <RequestVisitButtons claimId={claimId} onDone={load} openRequest={extOpen} />
        {/* Confirmar la LOR desde acá (dueño 2026-10-14): el mismo sello y el mismo paso a Claim in que en Ready to be filed. */}
        <LorConfirmButton claimId={claimId} onChanged={load} />
        {visitReqs.length > 0 && (
          <div style={{ display: 'grid', gap: 3, marginTop: 8 }}>
            {visitReqs.map((v) => (
              <div key={v.id} className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                <span style={v.canceled_at ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
                  <ClipboardList size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> <b>{v.label || v.type}</b> · {t('cd.requestedBy')} {v.requested_by || '—'} · {new Date(v.at).toLocaleString('en-US')}
                </span>
                {v.canceled_at && v.fulfilled ? (
                  /* La visita SE HIZO: el check-out del inspector cerró el pedido (2026-10-29). */
                  <div style={{ color: 'var(--green)', fontSize: 'var(--fs-2xs)', marginLeft: 18 }}>
                    ✓ {t('cd.reqDoneBy')} {v.canceled_by || '—'} · {new Date(v.canceled_at).toLocaleString('en-US')}
                  </div>
                ) : v.canceled_at ? (
                  /* El record QUEDA: eliminado por quién, cuándo y por qué. */
                  <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)', marginLeft: 18 }}>
                    <Ban size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.reqDeletedBy')} {v.canceled_by || '—'} · {new Date(v.canceled_at).toLocaleString('en-US')}
                    {v.cancel_reason ? <> — «{v.cancel_reason}»</> : null}
                  </div>
                ) : (
                  <button className="btn ghost sm" style={{ padding: '0 6px', fontSize: 'var(--fs-2xs)', marginLeft: 6, color: 'var(--red)' }}
                    title={t('cd.reqDeleteHint')} onClick={() => cancelVisitReq(v.id)}><Trash2 size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.reqDelete')}</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(atrSince || inspectorNotes) && (
        <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', fontSize: 'var(--fs-sm)' }}>
          {atrSince && <div><span className="muted">{t('cd.atrSince')}:</span> {new Date(atrSince).toLocaleDateString(localeUI())}</div>}
          {inspectorNotes && <div style={{ marginTop: atrSince ? 4 : 0 }}><span className="muted">{t('cd.inspectorNotes')}:</span> {inspectorNotes}</div>}
        </div>
      )}

      <div style={{ margin: '12px 0' }} />

      {appts.length === 0 && <div className="muted">{t('cd.noAppts')}</div>}
      {appts.map((a) => {
        const isTarget = searchParams.get('report') === a.id;
        const open = expanded.has(a.id) || isTarget; // link ?report=<id> auto-abre el reporte
        return (
          <div key={a.id} ref={isTarget ? reportRef : undefined}
            style={{ borderTop: '1px solid var(--line)', padding: '8px 0', ...(isTarget ? { background: 'var(--accent-soft)', borderRadius: 8 } : {}) }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={() => toggleExpand(a.id)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 0, cursor: 'pointer', padding: 0, textAlign: 'left', font: 'inherit' }}
              >
                {open ? <ChevronDown size={15} className="muted" /> : <ChevronRight size={15} className="muted" />}
                <span>
                  <b>{fmt(a.scheduled_start)}</b>
                  <span className="muted"> · {a.type}{a.inspector_name ? ` · ${a.inspector_name}` : ''}</span>
                </span>
              </button>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {/* Link explícito al inspection report (nombre del inspector + fecha ya arriba). */}
                <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => toggleExpand(a.id)}><FileText size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.inspectorReport')}</button>
                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <span className={`badge ${a.status === 'canceled' ? 'red' : a.status === 'completed' ? 'gray' : 'green'}`}>{a.status}</span>
                  {a.status === 'canceled' && a.canceled_by_name && (
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }} title={a.canceled_at ? fmt(a.canceled_at) : undefined}>
                      {t('cd.canceledBy')} {a.canceled_by_name}{a.canceled_at ? ` · ${fmt(a.canceled_at)}` : ''}
                    </span>
                  )}
                </span>
                {a.status === 'booked' && (
                  <>
                    <button className="btn ghost sm" onClick={() => { setReschedUrgent(false); setReschedAppt(a.id); }}>⟳ {t('sch.rescheduleShort')}</button>
                    <button className="btn ghost sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} title={t('sch.urgentHint')} onClick={() => { setReschedUrgent(true); setReschedAppt(a.id); }}>🚨 {t('sch.urgentReschedule')}</button>
                    <button className="btn ghost sm" onClick={() => setStatus(a.id, 'completed')}>{t('cd.complete')}</button>
                    <button className="btn ghost sm" onClick={() => setStatus(a.id, 'canceled')}>{t('cd.cancel')}</button>
                  </>
                )}
              </div>
            </div>
            {/* Notes for THIS inspection (por cita): el inspector ve esta nota, no la del claim. */}
            {a.status !== 'canceled' && (() => {
              const val = noteEdits[a.id] ?? a.notes ?? '';
              return (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                  <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><ClipboardList size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.notesThisInspection')}:</span>
                  <input className="input sm" style={{ flex: 1, minWidth: 180 }} placeholder={t('cd.notesThisInspectionPh')}
                    value={val} onChange={(e) => setNoteEdits((m) => ({ ...m, [a.id]: e.target.value }))} />
                  <button className="btn ghost sm" disabled={val === (a.notes ?? '')} onClick={() => saveNote(a.id, val)}>{t('cr.saveNotes')}</button>
                  {savedNote === a.id && <span style={{ color: 'var(--green)', fontSize: 'var(--fs-2xs)' }}><Check size={12} strokeWidth={1.75} /></span>}
                </div>
              );
            })()}
            {open && <ApptReport a={a} />}
          </div>
        );
      })}

      {scheduleOpen && (
        // El tipo a agendar = el de la ÚLTIMA solicitud (ej. ATR → cita 'atr' con su duración
        // propia). Sin solicitud previa, el modal usa 'inspection' por defecto.
        <ScheduleModal claimId={claimId} title={clientName || undefined}
          defaultType={visitReqs[0]?.type ? (REQUEST_TYPE_TO_APPT_TYPE[visitReqs[0].type] ?? undefined) : undefined}
          onClose={() => setScheduleOpen(false)} onDone={() => { setScheduleOpen(false); load(); }} />
      )}
      {reschedAppt && (
        <ScheduleModal claimId={claimId} appointmentId={reschedAppt} mode="reschedule" title={clientName || undefined} urgent={reschedUrgent}
          onClose={() => setReschedAppt(null)} onDone={() => { setReschedAppt(null); load(); }} />
      )}
    </div>
  );
}

/** Reporte del inspector (timeline GPS + notas + fotos + video) importado de ClaimRoute. */
function ApptReport({ a }: { a: Appt }) {
  const { t } = useI18n();
  const time = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-US') : null);
  // La OFICINA puede completar/corregir el reporte cuando el inspector no lo subió.
  const [editing, setEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [videoDraft, setVideoDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  async function saveReport() {
    setSaving(true);
    try {
      await api.post(`/api/appointments/${a.id}/report`, { resultNotes: notesDraft, videoUrl: videoDraft || null });
      a.result_notes = notesDraft || null; a.video_url = videoDraft || null; // refresco local
      setEditing(false); setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setSaving(false); }
  }
  const steps = [
    { k: t('cd.onMyWay'), v: time(a.on_my_way_at) },
    { k: t('cd.checkedIn'), v: time(a.checked_in_at) },
    { k: t('cd.started'), v: time(a.started_at) },
    { k: t('cd.checkedOut'), v: time(a.checked_out_at) },
  ].filter((s) => s.v);
  const photos = [
    { label: t('cd.checkPhoto'), url: a.check_photo_url },
    { label: t('cd.noShowPhoto'), url: a.no_show_photo_url },
  ].filter((p) => p.url) as { label: string; url: string }[];
  const videos = a.videos ?? [];
  const hasReport =
    a.result_notes || steps.length > 0 || photos.length > 0 || a.video_url || videos.length > 0 || a.reschedule_cause || a.leg_distance_miles;

  return (
    <div style={{ marginLeft: 21, marginTop: 8, fontSize: 'var(--fs-sm)', display: 'grid', gap: 8 }}>
      <div>
        <span className="muted">{t('cd.apptType')}:</span> {a.type} · <span className="muted">{a.progress}</span>
        {a.contractor_name && <> · <span className="muted">{t('cdw.contractor')}:</span> {a.contractor_name}</>}
        {a.leg_distance_miles != null && <> · {a.leg_distance_miles} mi</>}
      </div>

      {steps.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {steps.map((s) => (
            <span key={s.k} style={{ display: 'inline-flex', flexDirection: 'column', padding: '4px 10px', borderRadius: 8, background: 'var(--bg)' }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{s.k}</span>
              <span style={{ fontWeight: 600 }}>{s.v}</span>
            </span>
          ))}
        </div>
      )}

      {(a.reschedule_cause || a.reschedule_urgent) && <div>{a.reschedule_urgent && <span className="badge red" style={{ marginRight: 6 }}>🚨 {t('sch.urgentTag')}</span>}{a.reschedule_cause && <><span className="muted">{t('cd.rescheduleCause')}:</span> {a.reschedule_cause}</>}</div>}
      {a.notes && <div><span className="muted">{t('cd.notesPh')}:</span> {a.notes}</div>}
      {a.result_notes && (
        <div>
          <div className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('cd.inspectorReport')}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{a.result_notes}</div>
        </div>
      )}
      {/* Feedback del ATR (condición del techo + opinión del inspector) — para Review ATR. */}
      {(a.atr_roof_condition || a.atr_opinion) && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 10px', display: 'grid', gap: 4 }}>
          <div className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><Wrench size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.atrFeedback')}</div>
          {a.atr_roof_condition && <div><b>{t('cd.roofCondition')}:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{a.atr_roof_condition}</span></div>}
          {a.atr_opinion && <div><b>{t('cd.atrOpinion')}:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{a.atr_opinion}</span></div>}
        </div>
      )}

      {(photos.length > 0 || a.video_url || videos.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {photos.map((p) => (
            <a key={p.url} href={p.url} target="_blank" rel="noopener noreferrer" className="btn ghost sm"><Camera size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {p.label}</a>
          ))}
          {/* El link del ATR de la cita (YouTube o archivo). Si es un archivo, ya sale abajo con su nombre: no se repite. */}
          {a.video_url && !videos.some((v) => v.kind === 'atr' && a.video_url?.includes(`/api/atr-videos/${v.id}/media`)) && <a href={a.video_url} target="_blank" rel="noopener noreferrer" className="btn ghost sm"><Video size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.video')}</a>}
          {/* Los videos subidos desde el app de campo, pegados al reporte: el ATR y los informativos con su nombre. */}
          {videos.map((v) => (
            <a key={v.id} href={v.mediaUrl} target="_blank" rel="noopener noreferrer" className="btn ghost sm" title={v.fileName}>
              <Video size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {v.kind === 'atr' ? t('cd.videoAtr') : (v.title || t('cd.videoInfo'))}
            </a>
          ))}
        </div>
      )}

      {!hasReport && <div className="muted">{t('cd.noReport')}</div>}

      {/* Completar el reporte desde la OFICINA (cuando el inspector no lo subió del field app). */}
      {editing ? (
        <div style={{ display: 'grid', gap: 6, border: '1px dashed var(--line)', borderRadius: 8, padding: 8 }}>
          <textarea className="input" style={{ minHeight: 70, fontSize: 'var(--fs-xs)' }} placeholder={t('cd.reportNotesPh')} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} />
          <input className="input sm" placeholder={t('cd.reportVideoPh')} value={videoDraft} onChange={(e) => setVideoDraft(e.target.value)} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm" disabled={saving || !notesDraft.trim()} onClick={saveReport}>{saving ? '…' : t('common.save')}</button>
            <button className="btn ghost sm" onClick={() => setEditing(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn ghost sm" style={{ justifySelf: 'start' }}
            onClick={() => { setNotesDraft(a.result_notes ?? ''); setVideoDraft(a.video_url ?? ''); setEditing(true); }}>
            <Pencil size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {a.result_notes ? t('cd.editReport') : t('cd.addReport')}
          </button>
          {saved && <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--green)', fontWeight: 'var(--fw-semibold)' }}><Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cd.reportSaved')}</span>}
        </div>
      )}
    </div>
  );
}
